import { PropsWithChildren, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AdyenPaymentCompletedResult } from 'src/components/AdyenPayment/AdyenDropIn';
import {
  BuyerWithSalesDto,
  EventWithData,
  ListingDetails,
  SaleLineItemCoverage,
} from 'src/WebApiController';
import { createContext, useContext } from 'use-context-selector';

import {
  AdyenSession,
  useRetailSaleOperations,
} from './RetailSaleLocalContext.hooks';
import { RetailSaleFormValues } from './types';

export interface IRetailSaleContext {
  adyenSession: AdyenSession | null;
  selectedBuyer: BuyerWithSalesDto | undefined | null;
  disabled: boolean;
  onUpsertBuyer: () => Promise<boolean>;
  onPayAndComplete: () => void;
  onCompleteAsUnpaid: () => void;
  /** Submits the sale with external payment fields — skips the Adyen drop-in. */
  onCompleteExternalPayment: () => void;
  onAdyenPaymentCompleted: (result: AdyenPaymentCompletedResult) => void;
  onAdyenError: (error: { message: string }) => void;
  selectedStoredPaymentMethodId: string | null;
  setSelectedStoredPaymentMethodId: (id: string | null) => void;
  setSelectedBuyer: (buyer?: BuyerWithSalesDto) => void;
  isPartialPayment: boolean;
  setIsPartialPayment: (value: boolean) => void;
  partialAmount: number;
  setPartialAmount: (amount: number) => void;
  coverageDialogSaleId: number | null;
  coverageDialogSalesGroupId: string | null;
  onCoverageConfirmed: (coverages: SaleLineItemCoverage[]) => void;
  onCoverageDialogCancel: () => void;
  /** True when operating in multi-listing mode (ticketIds field present) */
  isMultiListing: boolean;
  /** Event of the single-listing flow — undefined in multi-listing mode. */
  event?: EventWithData;
}

export const RetailSaleLocalContext = createContext<IRetailSaleContext | null>(
  null
);

type RetailSaleLocalContextProviderProps = PropsWithChildren<{
  event?: EventWithData;
  listing?: ListingDetails;
}>;

export const RetailSaleLocalContextProvider = ({
  children,
  listing,
  event,
}: RetailSaleLocalContextProviderProps) => {
  const [selectedBuyer, setSelectedBuyer] = useState<BuyerWithSalesDto>();

  const [selectedStoredPaymentMethodId, setSelectedStoredPaymentMethodIdState] =
    useState<string | null>(null);

  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [partialAmount, setPartialAmount] = useState(0);

  // POS-8649 — Bug fix #2: multi-listing mode is determined by the PRESENCE
  // of the ticketIds field, not its length. The array may be empty while
  // listing details are still loading but the modal is already in multi-listing
  // mode, so checking length > 0 would incorrectly classify it as single-listing.
  //
  // Also accepts presence of `perListingSelections` so the multi-listing modal
  // stays in multi-listing mode even if a caller elects to seed only the
  // editable-selections field (e.g. once POS-8628 lands and the flat
  // ticketIds is derived at submit time only).
  const { watch } = useFormContext<RetailSaleFormValues>();
  const formTicketIds = watch('ticketIds');
  const formPerListingSelections = watch('perListingSelections');
  const isMultiListingFromForm =
    (formTicketIds !== undefined && formTicketIds !== null) ||
    (formPerListingSelections !== undefined &&
      formPerListingSelections !== null);

  const {
    disabled,
    adyenSession,
    onUpsertBuyer,
    onCompleteAsUnpaid,
    onCompleteExternalPayment,
    onPayAndComplete,
    onAdyenPaymentCompleted,
    onAdyenError,
    coverageDialogSaleId,
    coverageDialogSalesGroupId,
    onCoverageConfirmed,
    onCoverageDialogCancel,
  } = useRetailSaleOperations({
    event,
    listing,
    selectedBuyer,
    selectedStoredPaymentMethodId,
    setSelectedBuyer,
    isPartialPayment,
    partialAmount,
  });

  const value = useMemo<IRetailSaleContext>(
    () => ({
      adyenSession,
      selectedBuyer,
      disabled,
      onUpsertBuyer,
      onPayAndComplete,
      onCompleteAsUnpaid,
      onCompleteExternalPayment,
      onAdyenPaymentCompleted,
      onAdyenError,
      selectedStoredPaymentMethodId,
      setSelectedStoredPaymentMethodId: setSelectedStoredPaymentMethodIdState,
      setSelectedBuyer,
      isPartialPayment,
      setIsPartialPayment,
      partialAmount,
      setPartialAmount,
      coverageDialogSaleId,
      coverageDialogSalesGroupId,
      onCoverageConfirmed,
      onCoverageDialogCancel,
      isMultiListing: isMultiListingFromForm,
      event,
    }),
    [
      adyenSession,
      selectedBuyer,
      disabled,
      onUpsertBuyer,
      onPayAndComplete,
      onCompleteAsUnpaid,
      onCompleteExternalPayment,
      onAdyenPaymentCompleted,
      onAdyenError,
      selectedStoredPaymentMethodId,
      isPartialPayment,
      partialAmount,
      coverageDialogSaleId,
      coverageDialogSalesGroupId,
      onCoverageConfirmed,
      onCoverageDialogCancel,
      isMultiListingFromForm,
      event,
    ]
  );

  return (
    <RetailSaleLocalContext.Provider value={value}>
      {children}
    </RetailSaleLocalContext.Provider>
  );
};

export function useRetailSaleContext(): IRetailSaleContext {
  const ctx = useContext(RetailSaleLocalContext);
  if (!ctx) {
    throw new Error(
      'useRetailSaleContext must be used within RetailSaleLocalContextProvider'
    );
  }
  return ctx;
}
